/**
 * Conversation Detail Component
 * Displays all messages in a conversation with shadow metadata and infinite scroll
 */

import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageBubbleShadow } from '@/modules/chatbot/components/message-bubble';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';
import type { Message } from '@/types/shadow.types';
import { LoaderCircle } from 'lucide-react';

interface ConversationDetailProps {
  messages: Message[];
  isLoading?: boolean;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onMessageAnalyticsClick?: (messageId: string) => void;
  selectedMessageId?: string;
  header?: React.ReactNode;
}

const ActivityMessage = ({ message }: { message: Message }) => {
  const timestamp = new Date(message.createdAt);
  return (
    <div className='flex items-center gap-2 py-2 my-2 min-w-0 w-full'>
      <div className='flex-1 h-px bg-border' />
      <div
        className='text-xs text-muted-foreground px-3 py-1 bg-muted/50 rounded-full min-w-0'
        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
      >
        <span className='font-medium'>{message.senderType || 'System'}</span>
        {message.content && <span className='ml-1'>{message.content}</span>}
        <span className='ml-2 text-muted-foreground/70 whitespace-nowrap'>{format(timestamp, 'MMM d, yyyy h:mm a')}</span>
      </div>
      <div className='flex-1 h-px bg-border' />
    </div>
  );
};

export const ConversationDetail = ({
  messages,
  isLoading,
  hasMore,
  isFetchingNextPage,
  onLoadMore,
  onMessageAnalyticsClick,
  selectedMessageId
}: ConversationDetailProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll to bottom when messages first load
  useEffect(() => {
    if (messages.length > 0 && !isLoading && isInitialLoad.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        isInitialLoad.current = false;
      }, 100);
    }
  }, [messages.length, isLoading]);

  // Reset initial load flag when conversation changes (messages become empty then load new ones)
  useEffect(() => {
    if (messages.length === 0) {
      isInitialLoad.current = true;
    }
  }, [messages.length]);

  // Scroll to selected message when selectedMessageId changes
  useEffect(() => {
    if (selectedMessageId) {
      const messageElement = messageRefs.current.get(selectedMessageId);
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [selectedMessageId]);

  // Intersection Observer for infinite scroll (load older messages at top)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isFetchingNextPage && onLoadMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isFetchingNextPage, onLoadMore]);

  if (isLoading && messages.length === 0) {
    return (
      <div className='h-full flex items-center justify-center'>
        <LoaderCircle size={24} className='animate-spin mx-auto w-full' />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className='h-full flex items-center justify-center'>
        <div className='text-sm text-muted-foreground'>No messages found</div>
      </div>
    );
  }

  return (
    <div className='h-full overflow-hidden w-full min-w-0 bg-gray-100'>
      <ScrollArea className='h-full w-full' ref={scrollAreaRef}>
        <div className='p-4 pt-0 space-y-4 min-w-0 w-full bg-gray-100'>
          <div className='h-16' />
          {/* Infinite scroll trigger at top for loading older messages */}
          {hasMore && (
            <div ref={observerTarget} className='py-2 text-center'>
              {isFetchingNextPage ? (
                <div className='text-xs text-muted-foreground'>Loading older messages...</div>
              ) : (
                <div className='text-xs text-muted-foreground'>Scroll up for more</div>
              )}
            </div>
          )}
          {(() => {
            // Count agent responses for numbering
            let agentResponseCount = 0;
            return messages.map((message, index) => {
              const timestamp = new Date(message.createdAt);
              const isActivity = message.messageType === 'activity';
              const isUser = message.role === 'user';
              const isAgent = message.role === 'agent';
              const isAssistant = message.role === 'assistant';

              // Handle activity messages separately
              if (isActivity) {
                return <ActivityMessage key={message.id || message.messageId || index} message={message} />;
              }

              // Increment agent response counter for agent/assistant messages
              const isAgentResponse = isAgent || isAssistant;
              if (isAgentResponse) {
                agentResponseCount++;
              }
              const currentResponseNumber = agentResponseCount;

              // Convert Message type to the format expected by MessageBubble
              const chatMessage = {
                id: message.id || message.messageId,
                content: message.content,
                role: (isAgent || isAssistant ? 'assistant' : 'user') as 'user' | 'assistant',
                timestamp,
                channelId: undefined,
                shadowEval: message.shadowEval,
                contentAttributes: message.contentAttributes,
                additionalAttributes: message.additionalAttributes,
                contentType: message.contentType as
                  | 'text'
                  | 'input_text'
                  | 'cards'
                  | 'form'
                  | 'article'
                  | 'incoming_email'
                  | undefined,
                onAnalyticsClick: onMessageAnalyticsClick,
                isAnalyticsActive: selectedMessageId === (message.id || message.messageId),
                responseNumber: isAgentResponse ? currentResponseNumber : undefined
              };

              const messageId = message.id || message.messageId;

              return (
                <div
                  key={messageId || index}
                  ref={(el) => {
                    if (el && messageId) {
                      messageRefs.current.set(messageId, el);
                    }
                  }}
                  className='space-y-2 min-w-0 w-full'
                >
                  <div className={cn('flex items-start gap-3 min-w-0 w-full', isUser && 'flex-row')}>
                    <div className={cn('flex-1 min-w-0 w-full', isUser && 'flex flex-col items-start')}>
                      <MessageBubbleShadow message={chatMessage} />
                      {/* <div className={cn('text-xs text-muted-foreground mt-1 px-2 text-right', isUser && 'text-left')}>
                      {format(timestamp, 'MMM d, h:mm a')}
                      {message.role && (
                        <span className='ml-2'>
                          • {message.role === 'user' ? 'Customer' : "Agent"}
                        </span>
                      )}
                    </div> */}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
          {/* Invisible element at the bottom for auto-scroll */}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
    </div>
  );
};
